import * as React from "react";

/**
 * Standard page size option sets for pagination.
 * Export these constants to maintain consistency across tables.
 */
const PAGE_SIZE_OPTIONS_STANDARD = [10, 25, 50, 100] as const;
const PAGE_SIZE_OPTIONS_COMPACT = [5, 10, 20] as const;
const PAGE_SIZE_OPTIONS_LARGE = [50, 100, 200, 500] as const;

export const PAGE_SIZE_OPTIONS = {
  STANDARD: PAGE_SIZE_OPTIONS_STANDARD,
  COMPACT: PAGE_SIZE_OPTIONS_COMPACT,
  LARGE: PAGE_SIZE_OPTIONS_LARGE,
};

/**
 * Allowed page size option sets.
 * Use one of the predefined PAGE_SIZE_OPTIONS for consistency.
 */
export type TypeAllowedPageSizeOptions =
  | typeof PAGE_SIZE_OPTIONS_STANDARD
  | typeof PAGE_SIZE_OPTIONS_COMPACT
  | typeof PAGE_SIZE_OPTIONS_LARGE;

/**
 * Configuration for pagination controls with page navigation and size selection.
 *
 * @example
 * ```tsx
 * <Pagination
 *   paginationConfig={{
 *     initialPageSize: 25,
 *     displayType: 'dropdown',
 *     pageSizeOptions: PAGE_SIZE_OPTIONS.STANDARD,
 *     tableCountFunction: ({ startNum, endNum, totalRowCount }) =>
 *       `${startNum}-${endNum} of ${totalRowCount}`
 *   }}
 *   currentPageIndex={0}
 *   pageSize={25}
 *   totalRows={100}
 *   onPageChange={(index) => console.log('Page:', index)}
 *   onPageSizeChange={(size) => console.log('Page size:', size)}
 * />
 * ```
 */
/**
 * Props for the Pagination component.
 */
export interface PaginationProps {
  paginationConfig: TypePaginationConfig;
  currentPageIndex: number;
  pageSize: number;
  totalRows: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /**
   * Optional class name applied to the root container. Lets consumers layer
   * Tailwind utility overrides (e.g. `mx-200`, `p-300`) on top of the component
   * styles. Because `className` is not a styled-system prop, providing it keeps
   * the component on the Tailwind rendering path.
   */
  className?: string;
}

export interface TypePaginationConfig {
  /**
   * Initial number of rows per page.
   * @default 10
   */
  initialPageSize: number;
  /**
   * Function that returns the intl for displaying the table counts underneath a DataTable
   */
  tableCountFunction: (args: {
    startNum: number;
    endNum: number;
    totalRowCount: number;
  }) => React.ReactNode;
  /**
   * Display mode for page size selector.
   * - 'dropdown': Shows page size selector as a dropdown
   * Only used when pageSizeOptions is provided.
   * @default 'dropdown'
   */
  displayType?: "dropdown";
  /**
   * Optional array of page size options for the selector.
   * If not provided, page size selection will not be available.
   * Use PAGE_SIZE_OPTIONS constants for consistency.
   *
   * @example
   * pageSizeOptions: PAGE_SIZE_OPTIONS.STANDARD // [10, 25, 50, 100]
   */
  pageSizeOptions?: TypeAllowedPageSizeOptions;
  /**
   * Size variant for page number display.
   * - 'default': Shows current page ±1-2 pages (more visible pages)
   * - 'mini': Shows only current page (fewer visible pages)
   * @default 'default'
   */
  size?: "default" | "mini";
}
